import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { usePageSeo } from '../../../lib/seo'
import { getNewsItemBySlug, type NewsItem } from '../../../lib/siteContent'

const navigationItems = [
  { href: '/#about', label: '私たちについて' },
  { href: '/#services', label: 'サービス' },
  { href: '/#strengths', label: '強み' },
  { href: '/#company', label: '会社情報' },
]

export default function NewsDetail() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [item, setItem] = useState<NewsItem | null>(null)
  const { slug } = useParams<{ slug: string }>()

  usePageSeo({
    title: item ? `${item.title} | お知らせ | 合同会社サクラネクサスメディア` : 'お知らせ | 合同会社サクラネクサスメディア',
    description:
      item?.summary ??
      '合同会社サクラネクサスメディアのお知らせ詳細ページです。企業情報や更新情報をご案内しています。',
    path: slug ? `/news/${slug}` : '/news',
    ogType: item ? 'article' : 'website',
    noindex: !item && Boolean(slug),
  })

  useEffect(() => {
    if (!slug) {
      setItem(null)
      return
    }

    void getNewsItemBySlug(slug).then(setItem)
  }, [slug])

  return (
    <div className="bg-[#fcfeff] text-slate-700">
      <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="/" className="flex flex-col">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Corporate Site</p>
              <p className="text-sm font-semibold text-slate-800">合同会社サクラネクサスメディア</p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {navigationItems.map((navItem) => (
              <a key={navItem.href} href={navItem.href} className="text-sm font-medium text-slate-500 transition hover:text-sky-700">
                {navItem.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a href="/contact#contact-form" className="brand-button">
              お問い合わせ
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-sky-100 bg-white/90 text-sky-700 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-sky-100 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {navigationItems.map((navItem) => (
                <a
                  key={navItem.href}
                  href={navItem.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-sky-100 py-3 text-sm font-medium text-slate-600 last:border-b-0"
                >
                  {navItem.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>
        {item ? (
          <>
            <section className="section-light border-b border-sky-100 bg-[linear-gradient(180deg,#ffffff_0%,#f6fbff_100%)]">
              <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
                <a href="/news" className="text-sm font-medium text-slate-400 transition hover:text-sky-700">
                  お知らせ一覧へ戻る
                </a>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <span className="text-sm font-semibold text-slate-400">{item.publishedAt.replaceAll('-', '.')}</span>
                  <span className="inline-flex w-fit rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-600">
                    {item.category}
                  </span>
                </div>
                <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight text-slate-800 sm:text-5xl">{item.title}</h1>
                <p className="mt-6 max-w-3xl text-base leading-8 text-slate-500">{item.summary}</p>
              </div>
            </section>

            <section className="section-light bg-white">
              <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
                <article className="rounded-[2rem] border border-sky-100 bg-white p-8 text-sm leading-8 text-slate-600 shadow-[0_20px_50px_rgba(125,170,230,0.10)]">
                  {item.body.split('\n').map((paragraph) => (
                    <p key={paragraph} className="mb-6 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </article>
              </div>
            </section>
          </>
        ) : (
          <section className="section-light bg-white">
            <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8">
              <h1 className="text-3xl font-semibold text-slate-800">お知らせが見つかりませんでした</h1>
              <p className="mt-4 text-sm leading-7 text-slate-500">該当のお知らせは削除されたか、まだ公開されていない可能性があります。</p>
              <a href="/news" className="brand-button mt-8">
                お知らせ一覧へ戻る
              </a>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
